/*
 * Featured collection — pointer drag on the product card slider.
 *
 * Sense SliderComponent only wires arrow clicks + native overflow scroll.
 * It has no click-and-drag. This script adds press-and-drag for the mouse,
 * scoped to .featured-collection only. Touch keeps the native swipe.
 *
 * Only the image and the title are product links (treat-card-product.css).
 * Drag may start anywhere except the title and the controls; a drag that
 * started on the image swallows the click that follows, so it never opens
 * the product by accident.
 *
 * Behaviour:
 *   - pointerdown outside title / controls starts a potential drag
 *   - pointer capture begins only after a horizontal threshold (so brief
 *     presses do not steal events)
 *   - Add to bag / form controls / title link are excluded
 *   - scroll-snap is disabled while dragging
 */
(function initFeaturedCollectionSliderDrag() {
  const SECTION_ROOT_SELECTOR = '.featured-collection';
  const TRACK_SELECTOR = 'slider-component [id^="Slider-"]';
  const DRAG_THRESHOLD_PIXELS = 6;
  const DRAG_EXCLUDED_SELECTOR =
    'button, input, textarea, select, label, .quick-add, .slider-button, summary, .card__heading a, treat-card-variant-pills, .card-variant-pills';

  function bindTrackDrag(trackElement) {
    if (trackElement.dataset.fcDragBound === 'true') return;
    trackElement.dataset.fcDragBound = 'true';

    let pointerIsDown = false;
    let dragDidMove = false;
    let pointerCaptureIsActive = false;
    let activePointerId = null;
    let pointerStartClientX = 0;
    let scrollStartLeft = 0;

    function endPointerDrag(event) {
      if (!pointerIsDown) return;
      if (activePointerId !== null && event.pointerId !== activePointerId) return;

      pointerIsDown = false;

      if (
        pointerCaptureIsActive &&
        activePointerId !== null &&
        trackElement.hasPointerCapture(activePointerId)
      ) {
        trackElement.releasePointerCapture(activePointerId);
      }

      pointerCaptureIsActive = false;
      activePointerId = null;
      trackElement.classList.remove('is-pointer-dragging');

      /* Allow the next click after a completed drag to be suppressed once */
      if (dragDidMove) {
        window.setTimeout(() => {
          dragDidMove = false;
        }, 0);
      }
    }

    trackElement.addEventListener('pointerdown', (event) => {
      /* Mouse only: touch and pen use the native swipe (see section-featured-collection.css). */
      if (event.pointerType !== 'mouse' || event.button !== 0) return;
      if (event.target.closest(DRAG_EXCLUDED_SELECTOR)) return;

      pointerIsDown = true;
      dragDidMove = false;
      pointerCaptureIsActive = false;
      activePointerId = event.pointerId;
      pointerStartClientX = event.clientX;
      scrollStartLeft = trackElement.scrollLeft;
    });

    trackElement.addEventListener('pointermove', (event) => {
      if (!pointerIsDown || event.pointerId !== activePointerId) return;

      const horizontalDeltaPixels = event.clientX - pointerStartClientX;

      if (!dragDidMove && Math.abs(horizontalDeltaPixels) < DRAG_THRESHOLD_PIXELS) {
        return;
      }

      if (!pointerCaptureIsActive) {
        trackElement.setPointerCapture(event.pointerId);
        pointerCaptureIsActive = true;
      }

      dragDidMove = true;
      trackElement.classList.add('is-pointer-dragging');
      trackElement.scrollLeft = scrollStartLeft - horizontalDeltaPixels;
      event.preventDefault();
    });

    trackElement.addEventListener('pointerup', endPointerDrag);
    trackElement.addEventListener('pointercancel', endPointerDrag);
    trackElement.addEventListener('lostpointercapture', (event) => {
      if (event.pointerId !== activePointerId) return;
      pointerCaptureIsActive = false;
      endPointerDrag(event);
    });

    trackElement.addEventListener(
      'click',
      (event) => {
        if (!dragDidMove) return;
        /* Never block the title link — it is excluded from drag start */
        if (event.target.closest('.card__heading a')) return;
        event.preventDefault();
        event.stopPropagation();
        dragDidMove = false;
      },
      true
    );
  }

  function bindSectionSliders(sectionRootElement) {
    if (!sectionRootElement) return;
    sectionRootElement.querySelectorAll(TRACK_SELECTOR).forEach(bindTrackDrag);
  }

  function bindAllFeaturedCollectionSliders() {
    document.querySelectorAll(SECTION_ROOT_SELECTOR).forEach(bindSectionSliders);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', bindAllFeaturedCollectionSliders);
  } else {
    bindAllFeaturedCollectionSliders();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const sectionRootElement =
      event.target.matches?.(SECTION_ROOT_SELECTOR)
        ? event.target
        : event.target.querySelector?.(SECTION_ROOT_SELECTOR);
    bindSectionSliders(sectionRootElement);
  });
})();
